import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { 
  Plus, Package, Edit, Trash2, Search, Tag, Clock, Pound, 
  BarChart3, Car, Settings, Eye, ToggleLeft, ToggleRight,
  Hash, FileText, Target
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServicesManagementEnhanced = ({ getAuthHeaders }) => {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesWithCounts, setCategoriesWithCounts] = useState([]);
  const [suggestedCategories, setSuggestedCategories] = useState([]);
  const [vehicleClasses, setVehicleClasses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPricingDialogOpen, setIsPricingDialogOpen] = useState(false);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [selectedServiceForPricing, setSelectedServiceForPricing] = useState(null);
  const [servicePrices, setServicePrices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [selectedSuggestedCategories, setSelectedSuggestedCategories] = useState(new Set());
  const [isAddingSelectedCategories, setIsAddingSelectedCategories] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6'
  });
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    description: '',
    category: 'valet',
    service_type: 'service',
    tags: [],
    default_duration_minutes: '',
    default_price: '',
    cost: '',
    is_active: true,
    display_order: 0,
    barcode: '',
    gl_code: ''
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchServices();
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, categoryFilter, typeFilter]);

  const fetchInitialData = async () => {
    await Promise.all([
      fetchServices(),
      fetchCategories(),
      fetchCategoriesWithCounts(),
      fetchSuggestedCategories(),
      fetchVehicleClasses()
    ]);
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/service-categories`, getAuthHeaders());
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchCategoriesWithCounts = async () => {
    try {
      const response = await axios.get(`${API}/categories`, getAuthHeaders());
      setCategoriesWithCounts(response.data);
    } catch (error) {
      console.error('Failed to fetch categories with counts:', error);
    }
  };

  const fetchSuggestedCategories = async () => {
    try {
      const response = await axios.get(`${API}/suggested-categories`, getAuthHeaders());
      setSuggestedCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch suggested categories:', error);
    }
  };

  const fetchVehicleClasses = async () => {
    try {
      const response = await axios.get(`${API}/vehicle-classes`, getAuthHeaders());
      setVehicleClasses(response.data);
    } catch (error) {
      console.error('Failed to fetch vehicle classes:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('q', searchTerm);
      if (categoryFilter && categoryFilter !== '__ALL__') params.append('category', categoryFilter);
      if (typeFilter && typeFilter !== '__ALL__') params.append('service_type', typeFilter);
      params.append('active_only', 'false'); // Show all services

      const url = searchTerm || (categoryFilter && categoryFilter !== '__ALL__') || (typeFilter && typeFilter !== '__ALL__')
        ? `${API}/services/search?${params.toString()}`
        : `${API}/services`;
        
      console.log('Fetching services with URL:', url);
      console.log('Category filter:', categoryFilter);
      console.log('Params:', params.toString());
        
      const response = await axios.get(url, getAuthHeaders());
      setServices(response.data);
    } catch (error) {
      console.error('Fetch services error:', error);
      toast.error('Failed to fetch services');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Clean up form data
      const submitData = Object.keys(formData).reduce((acc, key) => {
        const value = formData[key];
        
        // Handle empty values
        if (value === '' || value === null || value === undefined) {
          // Set default values for required fields
          if (key === 'display_order') {
            acc[key] = 0; // Default display order
          }
          // Skip other empty fields
          return acc;
        } else if (key === 'default_price' || key === 'cost') {
          const numValue = parseFloat(value);
          if (!isNaN(numValue)) {
            acc[key] = numValue;
          }
        } else if (key === 'default_duration_minutes') {
          const numValue = parseInt(value);
          if (!isNaN(numValue)) {
            acc[key] = numValue;
          }
        } else if (key === 'display_order') {
          const numValue = parseInt(value);
          acc[key] = !isNaN(numValue) ? numValue : 0; // Default to 0 if invalid
        } else if (typeof value === 'boolean' || Array.isArray(value)) {
          acc[key] = value;
        } else {
          acc[key] = value;
        }
        
        return acc;
      }, {});

      if (editingService) {
        await axios.put(`${API}/services/${editingService.id}`, submitData, getAuthHeaders());
        toast.success('Service updated successfully');
      } else {
        await axios.post(`${API}/services`, submitData, getAuthHeaders());
        toast.success('Service created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchServices();
      fetchCategoriesWithCounts(); // Refresh category counts
    } catch (error) {
      console.error('Service operation error:', error);
      
      let errorMessage = 'Operation failed';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          if (Array.isArray(error.response.data.detail)) {
            errorMessage = error.response.data.detail
              .map(err => `${err.loc ? err.loc.join(' ') : 'Field'}: ${err.msg}`)
              .join(', ');
          } else {
            errorMessage = error.response.data.detail;
          }
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        await axios.delete(`${API}/services/${id}`, getAuthHeaders());
        toast.success('Service deleted successfully');
        fetchServices();
      } catch (error) {
        toast.error('Failed to delete service');
      }
    }
  };

  const toggleServiceStatus = async (service) => {
    try {
      const updateData = { ...service, is_active: !service.is_active };
      await axios.put(`${API}/services/${service.id}`, updateData, getAuthHeaders());
      toast.success(`Service ${updateData.is_active ? 'activated' : 'deactivated'} successfully`);
      fetchServices();
    } catch (error) {
      toast.error('Failed to update service status');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      sku: '',
      description: '',
      category: 'valet',
      service_type: 'service',
      tags: [],
      default_duration_minutes: '',
      default_price: '',
      cost: '',
      is_active: true,
      display_order: 0,
      barcode: '',
      gl_code: ''
    });
    setEditingService(null);
    setTagInput('');
  };

  const openEditDialog = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      sku: service.sku,
      description: service.description || '',
      category: service.category,
      service_type: service.service_type,
      tags: service.tags || [],
      default_duration_minutes: service.default_duration_minutes || '',
      default_price: service.default_price?.toString() || '',
      cost: service.cost?.toString() || '',
      is_active: service.is_active ?? true,
      display_order: service.display_order || 0,
      barcode: service.barcode || '',
      gl_code: service.gl_code || ''
    });
    setTagInput('');
    setIsDialogOpen(true);
  };

  const addTag = () => {
    if (tagInput && !formData.tags.includes(tagInput)) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const openPricingDialog = async (service) => {
    setSelectedServiceForPricing(service);
    await fetchServicePrices(service.id);
    setIsPricingDialogOpen(true);
  };

  const fetchServicePrices = async (serviceId) => {
    try {
      const response = await axios.get(`${API}/services/${serviceId}/prices`, getAuthHeaders());
      setServicePrices(response.data);
    } catch (error) {
      console.error('Failed to fetch service prices:', error);
      setServicePrices([]);
    }
  };

  const handlePriceUpdate = async (vehicleClass, price, duration) => {
    try {
      const priceData = {
        service_id: selectedServiceForPricing.id,
        vehicle_class: vehicleClass,
        price: parseFloat(price),
        duration_override_minutes: duration ? parseInt(duration) : null
      };

      await axios.post(`${API}/services/${selectedServiceForPricing.id}/prices`, priceData, getAuthHeaders());
      toast.success('Price updated successfully');
      await fetchServicePrices(selectedServiceForPricing.id);
    } catch (error) {
      toast.error('Failed to update price');
    }
  };

  // Category Management Functions
  const openCategoryDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryFormData({
        name: category.name.replace('_', ' '),
        description: category.description || '',
        color: category.color || '#3B82F6'
      });
    } else {
      setEditingCategory(null);
      setCategoryFormData({
        name: '',
        description: '',
        color: '#3B82F6'
      });
    }
    setSelectedSuggestedCategories(new Set());
    setIsCategoryDialogOpen(true);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        name: categoryFormData.name.trim(),
        description: categoryFormData.description.trim(),
        color: categoryFormData.color
      };

      if (editingCategory) {
        // Check if this is a name change and if category has services
        const originalName = editingCategory.name.replace('_', ' ');
        const newName = submitData.name;
        
        if (originalName.toLowerCase() !== newName.toLowerCase()) {
          // Name is changing, check for services
          try {
            const response = await axios.get(`${API}/categories/${editingCategory.id}/services-count`, getAuthHeaders());
            const { service_count } = response.data;
            
            if (service_count > 0) {
              const plural = service_count === 1 ? 'service' : 'services';
              const confirmMessage = `Changing this category name will update the category for ${service_count} ${plural} from "${originalName}" to "${newName}".\n\nAre you sure you want to continue?`;
              
              if (!window.confirm(confirmMessage)) {
                return; // User cancelled
              }
            }
          } catch (error) {
            console.error('Failed to check service count:', error);
            // Continue with update anyway if we can't check
          }
        }
        
        await axios.put(`${API}/categories/${editingCategory.id}`, submitData, getAuthHeaders());
        toast.success('Category updated successfully');
      } else {
        await axios.post(`${API}/categories`, submitData, getAuthHeaders());
        toast.success('Category created successfully');
      }

      setIsCategoryDialogOpen(false);
      fetchCategoriesWithCounts();
      fetchCategories(); // Refresh dropdown categories
    } catch (error) {
      let errorMessage = 'Operation failed';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      toast.error(errorMessage);
    }
  };

  const selectSuggestedCategory = (suggested) => {
    setCategoryFormData({
      name: suggested.name,
      description: suggested.description,
      color: '#3B82F6'
    });
  };

  const toggleSuggestedCategory = (suggested) => {
    const newSelected = new Set(selectedSuggestedCategories);
    const key = `${suggested.name}|${suggested.description}`;
    
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    
    setSelectedSuggestedCategories(newSelected);
  };

  const addSelectedCategories = async () => {
    if (selectedSuggestedCategories.size === 0) {
      toast.error('Please select at least one category to add');
      return;
    }

    setIsAddingSelectedCategories(true);
    const errors = [];
    let successCount = 0;

    try {
      // Convert selected categories to array of category objects
      const categoriesToAdd = Array.from(selectedSuggestedCategories).map(key => {
        const [name, description] = key.split('|');
        return {
          name: name.trim(),
          description: description.trim(),
          color: '#3B82F6'
        };
      });

      // Create each category
      for (const categoryData of categoriesToAdd) {
        try {
          await axios.post(`${API}/categories`, categoryData, getAuthHeaders());
          successCount++;
        } catch (error) {
          let errorMessage = `Failed to create "${categoryData.name}"`;
          if (error.response?.data?.detail) {
            errorMessage += `: ${error.response.data.detail}`;
          }
          errors.push(errorMessage);
        }
      }

      // Show results
      if (successCount > 0) {
        toast.success(`Successfully created ${successCount} categor${successCount === 1 ? 'y' : 'ies'}`);
        setSelectedSuggestedCategories(new Set());
        await fetchCategoriesWithCounts();
        await fetchCategories();
      }

      if (errors.length > 0) {
        errors.forEach(error => toast.error(error));
      }

      // Close dialog if all succeeded
      if (errors.length === 0) {
        setIsCategoryDialogOpen(false);
      }

    } catch (error) {
      toast.error('Failed to add selected categories');
    } finally {
      setIsAddingSelectedCategories(false);
    }
  };

  const clearSelectedCategories = () => {
    setSelectedSuggestedCategories(new Set());
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      // First check how many services are in this category
      const response = await axios.get(`${API}/categories/${categoryId}/services-count`, getAuthHeaders());
      const { category_name, service_count } = response.data;
      
      let confirmMessage;
      if (service_count > 0) {
        const plural = service_count === 1 ? 'service' : 'services';
        confirmMessage = `Cannot delete category "${category_name}" because it contains ${service_count} ${plural}.\n\nTo delete this category, you can either:\n1) Move all ${plural} to another category, or\n2) Rename this category if the new name is suitable for your needs.\n\nWould you like to rename this category instead?`;
        
        if (window.confirm(confirmMessage)) {
          // Open edit dialog for renaming
          const category = categoriesWithCounts.find(cat => cat.id === categoryId);
          if (category) {
            openCategoryDialog(category);
          }
        }
        return;
      } else {
        confirmMessage = `Are you sure you want to delete the category "${category_name}"? This cannot be undone.`;
      }
      
      if (window.confirm(confirmMessage)) {
        await axios.delete(`${API}/categories/${categoryId}`, getAuthHeaders());
        toast.success('Category deleted successfully');
        fetchCategoriesWithCounts();
        fetchCategories();
      }
    } catch (error) {
      let errorMessage = 'Failed to delete category';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      toast.error(errorMessage);
    }
  };

  const handleCategoryClick = (categoryName) => {
    console.log('Category clicked:', categoryName);
    if (selectedCategory === categoryName) {
      // If clicking the same category, show all
      setSelectedCategory('');
      setCategoryFilter('');
    } else {
      // Filter by selected category - use the raw category name as stored in DB
      setSelectedCategory(categoryName);
      setCategoryFilter(categoryName);
    }
  };

  const showAllServices = () => {
    setSelectedCategory('');
    setCategoryFilter('');
    setTypeFilter('');
    setSearchTerm('');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(price);
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getCategoryColor = (category) => {
    const colors = {
      valet: 'bg-blue-100 text-blue-700',
      detailing: 'bg-purple-100 text-purple-700',
      add_on: 'bg-green-100 text-green-700',
      ceramic: 'bg-yellow-100 text-yellow-700',
      protection: 'bg-orange-100 text-orange-700',
      interior: 'bg-pink-100 text-pink-700',
      exterior: 'bg-cyan-100 text-cyan-700',
      maintenance: 'bg-gray-100 text-gray-700',
      seasonal: 'bg-red-100 text-red-700',
      premium: 'bg-indigo-100 text-indigo-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  const getServiceTypeIcon = (type) => {
    switch (type) {
      case 'service': return <Package className="w-4 h-4" />;
      case 'add_on': return <Plus className="w-4 h-4" />;
      case 'product': return <Hash className="w-4 h-4" />;
      default: return <Package className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>
            Service Catalog
          </h2>
          <p className="text-gray-500">Manage your services, add-ons, and vehicle class pricing</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-gradient-to-r from-green-600 to-teal-600">
              <Plus className="w-4 h-4" />
              Add Service
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingService ? 'Edit Service' : 'Create Service'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Service Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g., Standard Valet"
                  />
                </div>
                <div>
                  <Label htmlFor="sku">SKU *</Label>
                  <Input
                    id="sku"
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value.toUpperCase() })}
                    required
                    placeholder="e.g., VAL-STD"
                  />
                </div>
                <div>
                  <Label htmlFor="category">Category</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>
                          {cat.charAt(0).toUpperCase() + cat.slice(1).replace('_', ' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="service_type">Type</Label>
                  <Select value={formData.service_type} onValueChange={(value) => setFormData({ ...formData, service_type: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="service">Service</SelectItem>
                      <SelectItem value="add_on">Add-on</SelectItem>
                      <SelectItem value="product">Product</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="default_price">Default Price (£) *</Label>
                  <Input
                    id="default_price"
                    type="number"
                    step="0.01"
                    value={formData.default_price}
                    onChange={(e) => setFormData({ ...formData, default_price: e.target.value })}
                    required
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label htmlFor="default_duration_minutes">Duration (minutes)</Label>
                  <Input
                    id="default_duration_minutes"
                    type="number"
                    value={formData.default_duration_minutes}
                    onChange={(e) => setFormData({ ...formData, default_duration_minutes: e.target.value })}
                    placeholder="180"
                  />
                </div>
                <div>
                  <Label htmlFor="cost">Cost (£)</Label>
                  <Input
                    id="cost"
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label htmlFor="display_order">Display Order</Label>
                  <Input
                    id="display_order"
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full min-h-20 px-3 py-2 border rounded-md"
                  placeholder="Customer-facing description of this service..."
                />
              </div>

              <div>
                <Label>Tags</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="Add tag (e.g., ceramic, premium)"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <Button type="button" size="sm" onClick={addTag}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                      {tag} ×
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="barcode">Barcode</Label>
                  <Input
                    id="barcode"
                    value={formData.barcode}
                    onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                    placeholder="Optional barcode"
                  />
                </div>
                <div>
                  <Label htmlFor="gl_code">GL Code</Label>
                  <Input
                    id="gl_code"
                    value={formData.gl_code}
                    onChange={(e) => setFormData({ ...formData, gl_code: e.target.value })}
                    placeholder="For accounting integration"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="is_active">Active (visible in booking systems)</Label>
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="flex-1">
                  {editingService ? 'Update Service' : 'Create Service'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Categories Management Section */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Service Categories</h3>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={showAllServices} className="gap-2">
              <Target className="w-4 h-4" />
              Show All Services
            </Button>
            <Dialog open={isCategoryDialogOpen} onOpenChange={(open) => { 
              setIsCategoryDialogOpen(open); 
              if (!open) {
                setEditingCategory(null);
                setSelectedSuggestedCategories(new Set());
              }
            }}>
              <DialogTrigger asChild>
                <Button size="sm" onClick={() => openCategoryDialog()} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Add Category
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{editingCategory ? 'Edit Category' : 'Create Service Category'}</DialogTitle>
                </DialogHeader>

                {!editingCategory && suggestedCategories.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-sm font-medium">Quick Add - Suggested Categories</Label>
                      <div className="flex gap-2">
                        {selectedSuggestedCategories.size > 0 && (
                          <>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={clearSelectedCategories}
                              className="text-xs"
                            >
                              Clear ({selectedSuggestedCategories.size})
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              onClick={addSelectedCategories}
                              disabled={isAddingSelectedCategories}
                              className="text-xs bg-green-600 hover:bg-green-700"
                            >
                              {isAddingSelectedCategories ? 'Adding...' : `Add Selected (${selectedSuggestedCategories.size})`}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className="max-h-64 overflow-y-auto border rounded-md p-2">
                      {/* Group categories by their category field */}
                      {Object.entries(
                        suggestedCategories.reduce((groups, suggested) => {
                          const group = suggested.category || 'Other';
                          if (!groups[group]) groups[group] = [];
                          groups[group].push(suggested);
                          return groups;
                        }, {})
                      ).map(([groupName, categoryItems]) => (
                        <div key={groupName} className="mb-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-2 px-2">{groupName}</h4>
                          <div className="grid grid-cols-1 gap-1">
                            {categoryItems.map((suggested) => {
                              const key = `${suggested.name}|${suggested.description}`;
                              const isSelected = selectedSuggestedCategories.has(key);
                              
                              return (
                                <div
                                  key={key}
                                  className={`flex items-center p-2 rounded border cursor-pointer transition-colors ${
                                    isSelected 
                                      ? 'border-blue-500 bg-blue-50' 
                                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                  }`}
                                  onClick={() => toggleSuggestedCategory(suggested)}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleSuggestedCategory(suggested)}
                                    className="mr-3 rounded"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm">{suggested.name}</div>
                                    <div className="text-xs text-gray-500 truncate">{suggested.description}</div>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="ml-2 text-xs opacity-70 hover:opacity-100"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      selectSuggestedCategory(suggested);
                                    }}
                                  >
                                    Edit
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex items-center my-4">
                      <div className="flex-1 border-t border-gray-200"></div>
                      <div className="px-3 text-sm text-gray-500">or create custom</div>
                      <div className="flex-1 border-t border-gray-200"></div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleCategorySubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="cat-name">Category Name *</Label>
                    <Input
                      id="cat-name"
                      value={categoryFormData.name}
                      onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                      required
                      placeholder="e.g., Premium Services"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cat-description">Description</Label>
                    <textarea
                      id="cat-description"
                      value={categoryFormData.description}
                      onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                      className="w-full min-h-20 px-3 py-2 border rounded-md"
                      placeholder="Optional description for this category..."
                    />
                  </div>
                  <div>
                    <Label htmlFor="cat-color">Category Color</Label>
                    <div className="flex gap-2 items-center">
                      <Input
                        id="cat-color"
                        type="color"
                        value={categoryFormData.color}
                        onChange={(e) => setCategoryFormData({ ...categoryFormData, color: e.target.value })}
                        className="w-20 h-10"
                      />
                      <span className="text-sm text-gray-500">Choose a color for this category</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1">
                      {editingCategory ? 'Update Category' : 'Create Category'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setIsCategoryDialogOpen(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {categoriesWithCounts.length === 0 ? (
          <Card className="p-8 text-center border-2 border-dashed border-gray-300">
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">No Service Categories Yet</h4>
            <p className="text-gray-500 mb-4">
              Create categories to organize your services (e.g., Valet, Detailing, Add-ons)
            </p>
            <Button onClick={() => openCategoryDialog()} className="gap-2">
              <Plus className="w-4 h-4" />
              Create Your First Category
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            {categoriesWithCounts.map((category) => (
              <Card
                key={category.id}
                className={`p-4 cursor-pointer transition-all hover:shadow-md border-2 ${
                  selectedCategory === category.name ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                }`}
                onClick={() => handleCategoryClick(category.name)}
              >
                <div className="flex items-center justify-between mb-2">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: category.color || '#3B82F6' }}
                  />
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="p-1 h-6 w-6"
                      onClick={(e) => {
                        e.stopPropagation();
                        openCategoryDialog(category);
                      }}
                    >
                      <Edit className="w-3 h-3" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="p-1 h-6 w-6 text-red-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCategory(category.id);
                      }}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div className="text-sm font-medium truncate" title={category.name}>
                  {category.name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {category.service_count} service{category.service_count !== 1 ? 's' : ''}
                </div>
              </Card>
            ))}
          </div>
        )}
      </Card>

      {/* Search and Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search services..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select 
            value={categoryFilter || undefined} 
            onValueChange={(value) => {
              const newFilter = value === '__ALL__' ? '' : value;
              setCategoryFilter(newFilter);
              setSelectedCategory(newFilter);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__ALL__">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1).replace('_', ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={typeFilter || undefined} onValueChange={(value) => setTypeFilter(value || '')}>
            <SelectTrigger>
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__ALL__">All Types</SelectItem>
              <SelectItem value="service">Services</SelectItem>
              <SelectItem value="add_on">Add-ons</SelectItem>
              <SelectItem value="product">Products</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => {
              setSearchTerm('');
              setCategoryFilter('');
              setTypeFilter('');
            }}>
              Clear Filters
            </Button>
          </div>
        </div>
      </Card>

      {/* Services Table */}
      {selectedCategory && (
        <div className="flex items-center gap-2 mb-4">
          <Badge variant="outline" className="text-sm">
            Showing: {(() => {
              const category = categoriesWithCounts.find(cat => cat.name === selectedCategory);
              return category?.display_name || selectedCategory.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
            })()} 
            ({services.length} services)
          </Badge>
          <Button variant="ghost" size="sm" onClick={showAllServices} className="text-sm">
            Clear filter
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12">Loading services...</div>
      ) : services.length === 0 ? (
        <Card className="p-12 text-center">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          {selectedCategory ? (
            <div>
              <p className="text-gray-500">No services found in "{selectedCategory.replace('_', ' ')}" category.</p>
              <Button variant="outline" className="mt-4" onClick={showAllServices}>
                Show All Services
              </Button>
            </div>
          ) : (
            <p className="text-gray-500">No services found. Add your first service!</p>
          )}
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Service</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {services.map((service) => (
                <TableRow key={service.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        {getServiceTypeIcon(service.service_type)}
                      </div>
                      <div>
                        <div className="font-medium">{service.name}</div>
                        {service.tags.length > 0 && (
                          <div className="flex gap-1 mt-1">
                            {service.tags.slice(0, 3).map(tag => (
                              <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                            ))}
                            {service.tags.length > 3 && (
                              <Badge variant="outline" className="text-xs">+{service.tags.length - 3}</Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-gray-600 max-w-md">
                      {service.description || <span className="italic text-gray-400">No description</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <code className="bg-gray-100 px-2 py-1 rounded text-sm">{service.sku}</code>
                  </TableCell>
                  <TableCell>
                    <Badge className={getCategoryColor(service.category)}>
                      {service.category.replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium text-green-600">
                      {formatPrice(service.default_price)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3 text-gray-400" />
                      <span className="text-sm">{formatDuration(service.default_duration_minutes)}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleServiceStatus(service)}
                      className={`text-xs ${service.is_active ? 'text-green-600' : 'text-gray-400'}`}
                    >
                      {service.is_active ? (
                        <>
                          <ToggleRight className="w-4 h-4 mr-1" />
                          Active
                        </>
                      ) : (
                        <>
                          <ToggleLeft className="w-4 h-4 mr-1" />
                          Inactive
                        </>
                      )}
                    </Button>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openPricingDialog(service)} title="Vehicle Class Pricing">
                        <Car className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(service)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(service.id)} className="text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Vehicle Class Pricing Dialog */}
      <Dialog open={isPricingDialogOpen} onOpenChange={setIsPricingDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              Vehicle Class Pricing - {selectedServiceForPricing?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Set different prices and durations for different vehicle classes. 
              Leave empty to use default pricing.
            </p>
            
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vehicle Class</TableHead>
                  <TableHead>Price (£)</TableHead>
                  <TableHead>Duration Override</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vehicleClasses.map((vehicleClass) => {
                  const existingPrice = servicePrices.find(p => p.vehicle_class === vehicleClass);
                  return (
                    <TableRow key={vehicleClass}>
                      <TableCell className="font-medium">
                        {vehicleClass.replace('_', ' ').toUpperCase()}
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder={formatPrice(selectedServiceForPricing?.default_price || 0)}
                          defaultValue={existingPrice?.price || ''}
                          onBlur={(e) => {
                            if (e.target.value) {
                              handlePriceUpdate(vehicleClass, e.target.value, existingPrice?.duration_override_minutes || '');
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          placeholder={`${selectedServiceForPricing?.default_duration_minutes || 0} mins`}
                          defaultValue={existingPrice?.duration_override_minutes || ''}
                          onBlur={(e) => {
                            handlePriceUpdate(vehicleClass, existingPrice?.price || selectedServiceForPricing?.default_price || 0, e.target.value);
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {existingPrice && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-600"
                            onClick={async () => {
                              try {
                                await axios.delete(`${API}/services/${selectedServiceForPricing.id}/prices/${vehicleClass}`, getAuthHeaders());
                                toast.success('Price removed');
                                await fetchServicePrices(selectedServiceForPricing.id);
                              } catch (error) {
                                toast.error('Failed to remove price');
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          
          <div className="flex justify-end">
            <Button onClick={() => setIsPricingDialogOpen(false)}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServicesManagementEnhanced;